import { useState } from "react";
import { api, openFile } from "@/lib/api";
import { useFetch } from "@/lib/useFetch";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader } from "@/mockups/ui";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Plus, Plane, CalendarCheck, CalendarClock, Hourglass, CheckCircle2, Clock3, XCircle, Trash2, Paperclip, FileText, MessageSquare } from "lucide-react";
import RequestVacationDialog from "@/components/rrhh/RequestVacationDialog";

const KPI_DEF = [
  { key: "assigned", label: "Días asignados", icon: Plane, accent: "text-primary bg-primary/12" },
  { key: "taken", label: "Disfrutados", icon: CalendarCheck, accent: "text-muted-foreground bg-muted" },
  { key: "pending", label: "Aprobados por disfrutar", icon: CalendarClock, accent: "text-chart-4 bg-chart-4/12" },
  { key: "requested", label: "Solicitados sin aprobar", icon: Hourglass, accent: "text-gold bg-gold/15" },
];

const STATUS = {
  approved: { label: "Aprobada", icon: CheckCircle2, cls: "bg-primary/12 text-primary" },
  pending: { label: "Pendiente", icon: Clock3, cls: "bg-gold/15 text-[#a9741c] dark:text-gold" },
  rejected: { label: "Rechazada", icon: XCircle, cls: "bg-destructive/12 text-destructive" },
};

const fmt = (d) => new Date(d).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" });

// El diálogo de solicitud vive en components/rrhh/RequestVacationDialog.jsx (reutilizado por Mi agenda).

export default function Vacaciones({ embedded = false }) {
  const { data, loading, refetch } = useFetch("/hr/me/vacations");
  const [dialogOpen, setDialogOpen] = useState(false);

  const cancel = async (id) => {
    if (!window.confirm("¿Cancelar esta solicitud?")) return;
    try { await api.delete(`/hr/me/vacations/${id}`); toast.success("Solicitud cancelada"); refetch(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const uploadJustif = async (id, e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post(`/hr/vacations/${id}/justificante`, fd);
      toast.success("Justificante adjuntado");
      refetch();
    } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
    e.target.value = "";
  };

  if (loading || !data) {
    return <div className="flex justify-center py-20"><div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" /></div>;
  }
  const s = data.summary;
  const reqs = data.requests || [];

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="vacaciones-page">
      {embedded ? (
        <div className="flex items-center justify-end">
          <Button size="sm" className="gap-1.5" onClick={() => setDialogOpen(true)} data-testid="request-vacation-btn"><Plus className="h-4 w-4" /> Solicitar días</Button>
        </div>
      ) : (
        <PageHeader title="Vacaciones y ausencias" subtitle="Solicita días y consulta el estado de tus peticiones">
          <Button size="sm" className="gap-1.5" onClick={() => setDialogOpen(true)} data-testid="request-vacation-btn"><Plus className="h-4 w-4" /> Solicitar días</Button>
        </PageHeader>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {KPI_DEF.map((k, i) => (
          <Card key={i} className="border-border shadow-none p-4 flex items-center gap-3" data-testid={`vac-kpi-${k.key}`}>
            <div className={cn("h-10 w-10 rounded-md flex items-center justify-center shrink-0", k.accent)}><k.icon className="h-5 w-5" /></div>
            <div>
              <div className="font-heading font-bold text-2xl tabular-nums leading-none">{s[k.key]}</div>
              <div className="text-xs text-muted-foreground mt-1 leading-tight">{k.label}</div>
            </div>
          </Card>
        ))}
      </div>

      <Card className="border-border shadow-none p-5">
        <div className="flex items-center justify-between mb-2 text-sm">
          <span className="font-medium">Balance anual {new Date().getFullYear()}</span>
          <span className="text-muted-foreground tabular-nums">{s.available} días disponibles de {s.assigned}</span>
        </div>
        <div className="h-2.5 rounded-full bg-muted overflow-hidden flex">
          <div className="h-full bg-primary" style={{ width: `${s.assigned ? (s.taken / s.assigned) * 100 : 0}%` }} />
          <div className="h-full bg-chart-4/70" style={{ width: `${s.assigned ? (s.pending / s.assigned) * 100 : 0}%` }} />
          <div className="h-full bg-gold/60" style={{ width: `${s.assigned ? (s.requested / s.assigned) * 100 : 0}%` }} />
        </div>
        <div className="flex flex-wrap gap-4 mt-3 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-primary" /> Disfrutados</span>
          <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-chart-4/70" /> Aprobados</span>
          <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-gold/60" /> Solicitados</span>
          <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-muted-foreground/30" /> Disponibles</span>
        </div>
      </Card>

      <Card className="border-border shadow-none overflow-hidden">
        <div className="p-5 pb-3 border-b border-border"><h3 className="font-heading font-bold text-base">Mis solicitudes</h3></div>
        {reqs.length === 0 ? (
          <div className="py-14 text-center text-sm text-muted-foreground">Aún no has solicitado días.</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="pl-5">Periodo</TableHead>
                <TableHead className="text-center">Días</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Justificante</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead className="w-10"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {reqs.map((r) => {
                const st = STATUS[r.status];
                return (
                  <TableRow key={r.id} data-testid={`vac-req-${r.id}`}>
                    <TableCell className="pl-5 text-sm font-medium tabular-nums">{r.from_date === r.to_date ? fmt(r.from_date) : `${fmt(r.from_date)} → ${fmt(r.to_date)}`}</TableCell>
                    <TableCell className="text-center text-sm tabular-nums">{r.days}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{r.type}</TableCell>
                    <TableCell>
                      {r.justificante ? (
                        <button className="text-xs text-primary hover:underline inline-flex items-center gap-1" onClick={() => openFile(`/hr/vacations/${r.id}/justificante`)} data-testid={`vac-justif-view-${r.id}`}>
                          <FileText className="h-3.5 w-3.5" /> Ver
                        </button>
                      ) : (
                        <label className={cn("cursor-pointer text-xs inline-flex items-center gap-1 hover:underline", r.justificante_required ? "text-gold" : "text-muted-foreground")} data-testid={`vac-justif-attach-${r.id}`}>
                          <Paperclip className="h-3.5 w-3.5" /> {r.justificante_required ? "Falta" : "Adjuntar"}
                          <input type="file" hidden accept="application/pdf,image/*,.doc,.docx" onChange={(e) => uploadJustif(r.id, e)} />
                        </label>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className={cn("inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium", st.cls)}><st.icon className="h-3.5 w-3.5" /> {st.label}</span>
                      {r.approver_message && (
                        <div className="text-[11px] text-muted-foreground mt-1 inline-flex items-start gap-1 max-w-[220px]"><MessageSquare className="h-3 w-3 mt-0.5 shrink-0" /> <span>{r.approver_message}</span></div>
                      )}
                    </TableCell>
                    <TableCell>
                      {r.status === "pending" && (
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => cancel(r.id)} data-testid={`vac-cancel-${r.id}`}><Trash2 className="h-4 w-4" /></Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Card>

      {dialogOpen && <RequestVacationDialog open={dialogOpen} onOpenChange={setDialogOpen} onSaved={refetch} />}
    </div>
  );
}
